// src/utils/apiClient.ts
var GenericApiClient = class {
  _config;
  // Should we require the version to specified or should we fallback to a default version like in blocks?
  constructor(config) {
    this._config = config;
  }
};

// src/utils/responseError.ts
var ResponseError = class extends Error {
  response;
  constructor(message, res) {
    super(message);
    this.response = res;
  }
};

// src/utils/fetchData.ts
async function fetchDataWithVersion(url, version, apiUrl, restInit) {
  try {
    const response = await fetch(`${apiUrl}${url}`, {
      ...restInit,
      headers: {
        "insurely-version": version,
        ...restInit?.headers
      }
    });
    if (!response.ok) {
      throw new ResponseError("Bad response", response);
    }
    return response.json();
  } catch (error) {
    return Promise.reject(error);
  }
}

// src/utils/versions.ts
var INSURELY_API_V3 = "2021-01-01";
var INSURELY_API_V4 = "2023-03-05";
var INSURELY_API_V5 = "2024-06-01";

// src/clients/wealth/utils/endpoints.ts
var initiateCollectionUrl = "/wealth/collection";
var supplementInfoUrl = (collectionId) => `/wealth/collection/${collectionId}/supplement-info`;
var addContactFormInputUrl = (collectionId) => `/wealth/collection/${collectionId}/add-contact-form-input`;
var getStatusUrl = (collectionId) => `/wealth/collection/${collectionId}/status`;
var getDataUrl = (collectionId) => `/wealth/collection/${collectionId}/data`;

// src/clients/wealth/handlers/addContactFormInput.ts
async function addContactFormInput(collectionId, body, config) {
  const { version, init: restInit, apiUrl } = config;
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    const init = { body: JSON.stringify(body), method: "POST", ...restInit };
    return fetchDataWithVersion(
      addContactFormInputUrl(collectionId),
      INSURELY_API_V5,
      apiUrl,
      init
    );
  }
  throw new Error(`Unsupported API version: ${version}`);
}

// src/clients/wealth/handlers/getData.ts
async function getData(collectionId, authToken, config) {
  const { version, init: restInit, apiUrl } = config;
  const init = {
    ...restInit,
    headers: {
      "Authorization-token": authToken,
      ...restInit.headers
    }
  };
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    return fetchDataWithVersion(
      getDataUrl(collectionId),
      INSURELY_API_V5,
      apiUrl,
      init
    );
  }
  throw new Error(`Unsupported API version: ${version}`);
}

// src/clients/wealth/handlers/getStatus.ts
async function getStatus(collectionId, config) {
  const { version, init, apiUrl } = config;
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    return fetchDataWithVersion(
      getStatusUrl(collectionId),
      INSURELY_API_V5,
      apiUrl,
      init
    );
  }
  throw new Error(`Unsupported API version: ${version}`);
}

// src/clients/wealth/handlers/initiateCollection.ts
async function initiateCollection(body, config) {
  const { version, init: restInit, apiUrl } = config;
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    const init = { body: JSON.stringify(body), method: "POST", ...restInit };
    return fetchDataWithVersion(
      initiateCollectionUrl,
      INSURELY_API_V5,
      apiUrl,
      init
    );
  }
  throw new Error(`Unsupported API version: ${version}`);
}

// src/clients/wealth/handlers/supplementInfo.ts
async function supplementInfo(collectionId, body, config) {
  const { version, init: restInit, apiUrl } = config;
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    const init = { body: JSON.stringify(body), method: "POST", ...restInit };
    return fetchDataWithVersion(supplementInfoUrl(collectionId), INSURELY_API_V5, apiUrl, init);
  }
  throw new Error(`Unsupported API version: ${version}`);
}

// src/clients/wealth/mappers/getData.ts
import { Wealth } from "@insurely/types";
function mapToGetDataModel(data, version) {
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    return data.map((financialProduct) => {
      switch (financialProduct.type) {
        case Wealth.financialProductType.CUSTODY_ACCOUNT: {
          const {
            accountInformation,
            company,
            companyDisplayName,
            financialInformation,
            holder,
            holdings,
            id,
            monthlySavingsAmount,
            personalInformation,
            productName,
            productNumber,
            type
          } = financialProduct;
          return {
            accountInformation,
            company,
            companyDisplayName,
            financialInformation,
            holder,
            holdings,
            id,
            monthlySavingsAmount,
            personalInformation,
            productName,
            productNumber,
            type
          };
        }
        case Wealth.financialProductType.ISK: {
          const {
            accountInformation,
            company,
            companyDisplayName,
            financialInformation,
            holder,
            holdings,
            id,
            monthlySavingsAmount,
            personalInformation,
            productName,
            productNumber,
            type
          } = financialProduct;
          return {
            accountInformation,
            company,
            companyDisplayName,
            financialInformation,
            holder,
            holdings,
            id,
            monthlySavingsAmount,
            personalInformation,
            productName,
            productNumber,
            type
          };
        }
        case Wealth.financialProductType.UNKNOWN_INVESTMENT_TYPE: {
          const {
            accountInformation,
            company,
            companyDisplayName,
            financialInformation,
            holder,
            holdings,
            id,
            monthlySavingsAmount,
            personalInformation,
            productName,
            productNumber,
            type
          } = financialProduct;
          return {
            accountInformation,
            company,
            companyDisplayName,
            financialInformation,
            holder,
            holdings,
            id,
            monthlySavingsAmount,
            personalInformation,
            productName,
            productNumber,
            type
          };
        }
        case Wealth.financialProductType.KF: {
          const {
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            personalInformation,
            productName,
            productNumber,
            traditionallyManaged,
            type
          } = financialProduct;
          return {
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            personalInformation,
            productName,
            productNumber,
            traditionallyManaged,
            type
          };
        }
        case Wealth.financialProductType.COLLECTIVE_OCCUPATIONAL_PENSION: {
          const {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          } = financialProduct;
          return {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          };
        }
        case Wealth.financialProductType.IPS_PENSION: {
          const {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          } = financialProduct;
          return {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          };
        }
        case Wealth.financialProductType.OCCUPATIONAL_PENSION: {
          const {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          } = financialProduct;
          return {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          };
        }
        case Wealth.financialProductType.PRIVATE_PENSION: {
          const {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          } = financialProduct;
          return {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          };
        }
        case Wealth.financialProductType.UNKNOWN_PENSION_TYPE: {
          const {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          } = financialProduct;
          return {
            activePayment,
            activePayout,
            classification,
            company,
            companyDisplayName,
            fees,
            financialInformation,
            holder,
            holdings,
            id,
            insurance,
            movability,
            otherInsuredPerson,
            payoutDetails,
            personalInformation,
            productName,
            productNumber,
            startDate,
            traditionallyManaged,
            type
          };
        }
        default:
          throw Error(`Unsupported type: ${financialProduct.type}`);
      }
    });
  }
  throw new Error(`Unsupported version: ${version}`);
}

// src/clients/wealth/mappers/getStatus.ts
function mapToGetStatusModel(data, version) {
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    const { company, extraInformation, id, status } = data;
    return { company, extraInformation, id, status: status?.replaceAll("_", " ") };
  }
  throw new Error(`Unsupported version: ${version}`);
}

// src/clients/wealth/mappers/initiateCollection.ts
function mapToInitiateCollectionModel(data, version) {
  if ([INSURELY_API_V3, INSURELY_API_V4, INSURELY_API_V5].includes(version)) {
    const { company, extraInformation, id, status } = data;
    return { company, extraInformation, id, status };
  }
  throw new Error(`Unsupported version: ${version}`);
}

// src/clients/wealth/index.ts
var WealthApiClient = class extends GenericApiClient {
  initiateCollection = async (body) => {
    const data = await initiateCollection(body, this._config);
    return mapToInitiateCollectionModel(data, this._config.version);
  };
  supplementInfo = async (collectionId, body) => supplementInfo(collectionId, body, this._config);
  addContactFormInput = async (collectionId, body) => addContactFormInput(collectionId, body, this._config);
  getStatus = async (collectionId) => {
    const data = await getStatus(collectionId, this._config);
    return mapToGetStatusModel(data, this._config.version);
  };
  getData = async (collectionId, authToken) => {
    const data = await getData(collectionId, authToken, this._config);
    return mapToGetDataModel(data, this._config.version);
  };
};
export {
  INSURELY_API_V3,
  INSURELY_API_V4,
  INSURELY_API_V5,
  ResponseError,
  WealthApiClient
};
//# sourceMappingURL=index.mjs.map